import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { useTheme } from '../contexts/Themecontext';
import AdminEmailDashboard from './AdminEmailDashboard/AdminEmailDashboard';
import ResumeManagement from './AdminEmailDashboard/ResumeManagement';
import { 
    Users, Briefcase, PlusCircle, ClipboardList, Trash2,
    LayoutDashboard, CheckCircle, Clock, Send, Mail, X, 
    UserSearch, Phone, Link as LinkIcon, ExternalLink, GraduationCap, 
    MapPin, DollarSign, Download, Eye, Loader, AlertCircle,FileText
} from 'lucide-react';

// Helper Components
const InputGroup = ({ label, placeholder, value, onChange, classes, required = false }) => (
    <div className="space-y-2">
        <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-2">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input 
            className={`w-full p-4 rounded-xl ${classes.input}`} 
            placeholder={placeholder} 
            value={value || ''} 
            onChange={onChange}
            required={required}
        />
    </div>
);

const TextAreaGroup = ({ label, placeholder, value, onChange, classes, rows = 6, required = false }) => (
    <div className="space-y-2">
        <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-2">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <textarea
            className={`w-full p-4 rounded-xl ${classes.input} font-mono text-sm`}
            placeholder={placeholder}
            value={value || ''}
            onChange={onChange}
            rows={rows}
            required={required}
        />
    </div>
);

const InfoBox = ({ icon: Icon, label, value, isLink, classes }) => (
    <div className="p-6 rounded-2xl bg-white/5 border border-white/5">
        <div className="flex items-center gap-3 mb-2">
            <Icon size={16} className="text-purple-500" />
            <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">{label}</p>
        </div>
        {isLink && value ? (
            <a href={value} target="_blank" rel="noreferrer" className="text-sm font-bold text-blue-400 hover:underline truncate block">
                {value}
            </a>
        ) : (
            <p className="text-sm font-bold text-white">{value || 'N/A'}</p>
        )}
    </div>
);

const SkillBadge = ({ label, value, icon: Icon, color }) => (
    <div className={`p-4 rounded-2xl border border-${color}-500/20 bg-${color}-500/5 flex items-center gap-4`}>
        <div className={`p-2 rounded-lg bg-${color}-500 text-white shadow-lg shadow-${color}-500/20`}>
            <Icon size={18} />
        </div>
        <div>
            <p className="text-[9px] font-black uppercase opacity-50">{label}</p>
            <p className="text-sm font-black text-white">{value}</p>
        </div>
    </div>
);

const NavButton = ({ icon, label, active, onClick, classes }) => (
    <button 
        onClick={onClick} 
        className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all ${active ? classes.tabActive : classes.tabInactive}`}
    >
        {icon} <span className="font-black text-[11px] uppercase tracking-widest">{label}</span>
    </button>
);

const StatCard = ({ icon: Icon, label, value, color, classes }) => (
    <div className={`${classes.card} border ${classes.border} p-6 rounded-3xl relative overflow-hidden`}>
        <div className={`absolute top-0 right-0 p-8 opacity-10 text-${color}-500`}>
            <Icon size={80} />
        </div>
        <p className={`text-[10px] font-black uppercase tracking-widest ${classes.textMuted}`}>{label}</p>
        <h3 className={`text-4xl font-black mt-2 ${classes.text}`}>{value}</h3>
    </div>
);

// Tab Components
const OverviewTab = ({ stats, applications, classes }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard icon={Users} label="Total Users" value={stats?.total_users || 0} color="blue" classes={classes} />
        <StatCard icon={Briefcase} label="Job Openings" value={stats?.total_jobs || 0} color="purple" classes={classes} />
        <StatCard icon={ClipboardList} label="Applications" value={stats?.total_applications || 0} color="green" classes={classes} />
        <StatCard icon={Clock} label="Pending" value={applications.filter(a => a.status === 'applied').length} color="orange" classes={classes} />
    </div>
);

const JobsTab = ({ jobs, classes, onEdit, onDelete }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {jobs.length === 0 ? (
            <div className="col-span-2 p-12 text-center">
                <Briefcase size={48} className={`${classes.textMuted} mx-auto mb-4`} />
                <p className={classes.textMuted}>No jobs posted yet</p>
            </div>
        ) : (
            jobs.map(job => (
                <div key={job.id || job._id} className={`${classes.card} border ${classes.border} p-8 rounded-[2rem] flex flex-col justify-between hover:border-purple-500/50 transition-colors`}>
                    <div>
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="text-xl font-black text-white uppercase tracking-tighter leading-tight">{job.title}</h3>
                            <span className="px-3 py-1 bg-white/5 text-zinc-500 rounded-lg text-[10px] font-black uppercase">{job.type}</span>
                        </div>
                        <p className="text-xs text-purple-500 font-bold mb-6">{job.department} • {job.location}</p>
                        {job.salary_range && (
                            <div className="flex items-center gap-2 text-zinc-400 text-sm mb-4">
                                <DollarSign size={16} /> <span>{job.salary_range}</span>
                            </div>
                        )}
                    </div>
                    <div className="flex gap-3 pt-6 border-t border-white/5">
                        <button 
                            onClick={() => onEdit && onEdit(job)}
                            className="flex-1 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-xs font-black uppercase text-white transition-all"
                        >
                            Edit Role
                        </button>
                        <button 
                            onClick={() => onDelete && onDelete(job.id || job._id)}
                            className="p-3 bg-red-500/10 text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition-all"
                        >
                            <Trash2 size={18} />
                        </button>
                    </div>
                </div>
            ))
        )}
    </div>
);

const PostJobTab = ({ jobForm, setJobForm, classes, onSubmit, loading }) => {
    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit();
    };

    return (
        <form onSubmit={handleSubmit} className={`${classes.card} p-10 rounded-[2.5rem] border ${classes.border} space-y-8 max-w-4xl mx-auto shadow-2xl`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputGroup 
                    label="Job Title" 
                    placeholder="Senior Backend Architect" 
                    value={jobForm.title} 
                    onChange={e => setJobForm({...jobForm, title: e.target.value})} 
                    classes={classes}
                    required
                />
                <InputGroup 
                    label="Department" 
                    placeholder="Engineering" 
                    value={jobForm.department} 
                    onChange={e => setJobForm({...jobForm, department: e.target.value})} 
                    classes={classes}
                    required
                />
                
                <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-2">Job Location *</label>
                    <select 
                        className={`w-full p-4 rounded-xl ${classes.input}`} 
                        value={jobForm.location} 
                        onChange={e => setJobForm({...jobForm, location: e.target.value})}
                        required
                    >
                        <option value="Remote">Fully Remote</option>
                        <option value="Hybrid">Hybrid</option>
                        <option value="Pune, India">Pune HQ</option>
                        <option value="On-site">On-site</option>
                    </select>
                </div>

                <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-2">Job Type *</label>
                    <select 
                        className={`w-full p-4 rounded-xl ${classes.input}`} 
                        value={jobForm.type} 
                        onChange={e => setJobForm({...jobForm, type: e.target.value})}
                        required
                    >
                        <option value="Full-time">Full-time</option>
                        <option value="Part-time">Part-time</option>
                        <option value="Contract">Contract</option>
                        <option value="Internship">Internship</option>
                    </select>
                </div>
            </div>

            <InputGroup 
                label="Salary Range" 
                placeholder="₹18L - ₹35L" 
                value={jobForm.salary_range} 
                onChange={e => setJobForm({...jobForm, salary_range: e.target.value})} 
                classes={classes}
            />

            <TextAreaGroup
                label="Short Description"
                placeholder="Brief overview of the role..."
                value={jobForm.description}
                onChange={e => setJobForm({...jobForm, description: e.target.value})}
                classes={classes}
                rows={3}
                required
            />

            <TextAreaGroup
                label="Full Description"
                placeholder="Detailed job description..."
                value={jobForm.full_description}
                onChange={e => setJobForm({...jobForm, full_description: e.target.value})}
                classes={classes}
                rows={6}
                required
            />

            <TextAreaGroup
                label="Requirements (One per line)"
                placeholder="- 5+ years of Go programming&#10;- Experience with MongoDB&#10;- AWS expertise"
                value={jobForm.requirements}
                onChange={e => setJobForm({...jobForm, requirements: e.target.value})}
                classes={classes}
                rows={5}
            />

            <TextAreaGroup
                label="Responsibilities (One per line)"
                placeholder="- Design and implement backend systems&#10;- Lead technical decisions"
                value={jobForm.responsibilities}
                onChange={e => setJobForm({...jobForm, responsibilities: e.target.value})}
                classes={classes}
                rows={5}
            />

            <button 
                type="submit" 
                disabled={loading}
                className={`w-full py-5 rounded-2xl ${classes.button} font-black uppercase tracking-[0.2em] text-lg transition-transform hover:scale-[1.01] disabled:opacity-50 flex items-center justify-center gap-3`}
            >
                {loading ? (
                    <>
                        <Loader className="w-6 h-6 animate-spin" />
                        Creating...
                    </>
                ) : (
                    'Launch Job to Public'
                )}
            </button>
        </form>
    );
};

const ApplicationsTab = ({ applications, classes, onSelect }) => (
    <div className={`${classes.card} border ${classes.border} rounded-3xl overflow-hidden`}>
        <div className="p-6 border-b border-white/5 bg-white/5 flex justify-between items-center">
            <h2 className={`font-black uppercase tracking-tighter text-xl ${classes.text}`}>Recent Applications</h2>
            <span className="px-3 py-1 bg-purple-500/10 text-purple-500 rounded-full text-xs font-bold">{applications.length} Total</span>
        </div>
        {applications.length === 0 ? (
            <div className="p-12 text-center">
                <ClipboardList size={48} className={`${classes.textMuted} mx-auto mb-4`} />
                <p className={classes.textMuted}>No applications yet</p>
            </div>
        ) : (
            <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead>
                        <tr className={`text-[10px] font-black uppercase ${classes.textMuted} border-b ${classes.border}`}>
                            <th className="p-6">Applicant</th>
                            <th className="p-6">Role</th>
                            <th className="p-6">Experience</th>
                            <th className="p-6">Status</th>
                            <th className="p-6 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {applications.map(app => (
                            <tr key={app.id || app._id} className={`border-b ${classes.border} hover:bg-white/5 transition-colors`}>
                                <td className="p-6">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center font-bold text-white shadow-lg">
                                            {app.first_name?.[0] || app.email?.[0]?.toUpperCase() || '?'}
                                        </div>
                                        <div>
                                            <p className={`font-bold ${classes.text}`}>{app.first_name} {app.last_name}</p>
                                            <p className="text-xs text-zinc-500">{app.email}</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-6">
                                    <p className={`font-bold text-sm ${classes.text}`}>{app.job_title}</p>
                                </td>
                                <td className="p-6">
                                    <span className={`text-sm font-bold ${classes.text}`}>{app.years_experience || 0} Years</span>
                                </td>
                                <td className="p-6">
                                    <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${
                                        app.status === 'offer' ? 'bg-green-500/20 text-green-500' : 
                                        app.status === 'rejected' ? 'bg-red-500/20 text-red-500' :
                                        app.status === 'interview' ? 'bg-purple-500/20 text-purple-500' :
                                        'bg-orange-500/20 text-orange-500'
                                    }`}>{app.status || 'applied'}</span>
                                </td>
                                <td className="p-6 text-right">
                                    <button 
                                        onClick={() => onSelect(app)} 
                                        className="p-2 bg-purple-600 rounded-lg text-white hover:scale-110 transition-transform"
                                    >
                                        <Eye size={18} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </div>
);

const DetailModal = ({ app, classes, onClose, onUpdate }) => (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl overflow-y-auto">
        <div className={`${classes.card} w-full max-w-5xl max-h-[90vh] rounded-[2.5rem] border ${classes.border} relative flex flex-col overflow-hidden shadow-[0_0_50px_rgba(168,85,247,0.15)]`}>
            <div className="p-8 md:p-12 border-b border-white/5 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div className="flex items-center gap-6">
                    <div className="w-24 h-24 rounded-[2rem] bg-gradient-to-tr from-purple-600 to-pink-600 p-1">
                        <div className="w-full h-full bg-black rounded-[1.8rem] flex items-center justify-center text-4xl font-black text-white">
                            {app.profile_pic_url ? (
                                <img src={app.profile_pic_url} className="w-full h-full object-cover rounded-[1.8rem]" alt="Profile" />
                            ) : (
                                <span>{app.first_name?.[0] || '?'}</span>
                            )}
                        </div>
                    </div>
                    <div>
                        <h2 className="text-4xl font-black tracking-tighter text-white uppercase">{app.first_name} {app.last_name}</h2>
                        <div className="flex gap-4 mt-2">
                            <span className="flex items-center gap-2 text-purple-500 text-xs font-black uppercase">
                                <MapPin size={14}/> {app.location || 'Remote'}
                            </span>
                            <span className="flex items-center gap-2 text-zinc-500 text-xs font-black uppercase">
                                <Clock size={14}/> Applied {new Date(app.created_at).toLocaleDateString()}
                            </span>
                        </div>
                    </div>
                </div>
                <button onClick={onClose} className="p-3 bg-white/5 rounded-full hover:bg-white/10 text-white transition-all">
                    <X size={24} />
                </button>
            </div>
            <div className="flex-1 overflow-y-auto p-8 md:p-12 space-y-12">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                    <div className="lg:col-span-2 space-y-10">
                        {app.cover_letter && (
                            <section>
                                <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-purple-500 mb-6">Cover Letter</h3>
                                <div className="p-8 bg-white/5 rounded-3xl border border-white/5">
                                    <p className="text-lg leading-relaxed text-zinc-300 italic">"{app.cover_letter}"</p>
                                </div>
                            </section>
                        )}
                        <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <InfoBox icon={Mail} label="Email" value={app.email} classes={classes} />
                            <InfoBox icon={Phone} label="Phone" value={app.phone} classes={classes} />
                            <InfoBox icon={LinkIcon} label="LinkedIn" value={app.linkedin_url} isLink classes={classes} />
                            <InfoBox icon={ExternalLink} label="Portfolio" value={app.portfolio_url} isLink classes={classes} />
                        </section>
                    </div>
                    <div className="space-y-6">
                        <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-purple-500 mb-6">Details</h3>
                        <div className="space-y-4">
                            <SkillBadge label="Experience" value={`${app.years_experience || 0} Years`} icon={Briefcase} color="purple" />
                            <SkillBadge label="Role" value={app.job_title} icon={GraduationCap} color="pink" />
                            <SkillBadge label="Source" value={app.source || 'Direct'} icon={Send} color="orange" />
                        </div>
                        {app.resume_url && (
                            <a href={app.resume_url} target="_blank" rel="noreferrer" className="mt-8 w-full flex items-center justify-center gap-4 py-5 rounded-2xl bg-white text-black font-black uppercase tracking-widest hover:bg-purple-500 hover:text-white transition-all shadow-xl">
                                <Download size={20}/> Download Resume
                            </a>
                        )}
                    </div>
                </div>
            </div>
            <div className="p-8 border-t border-white/5 bg-black/50 flex gap-4">
                <button 
                    onClick={() => onUpdate(app.id || app._id, 'screening')} 
                    className="flex-1 py-5 bg-green-600 hover:bg-green-500 text-white rounded-2xl font-black uppercase tracking-widest transition-all flex items-center justify-center gap-3"
                >
                    <CheckCircle size={22} /> Move Forward
                </button>
                <button 
                    onClick={() => onUpdate(app.id || app._id, 'rejected')} 
                    className="flex-1 py-5 bg-zinc-800 hover:bg-red-900/40 text-zinc-400 hover:text-red-500 rounded-2xl font-black uppercase tracking-widest transition-all"
                >
                    Reject
                </button>
            </div>
        </div>
    </div>
);

const UsersTab = ({ users, classes }) => (
    <div className={`${classes.card} border ${classes.border} rounded-3xl overflow-hidden`}>
        <div className="p-6 border-b border-white/5 bg-white/5">
            <h2 className="font-black uppercase tracking-tighter text-xl text-white">Registered Users</h2>
        </div>
        {users.length === 0 ? (
            <div className="p-12 text-center">
                <Users size={48} className={`${classes.textMuted} mx-auto mb-4`} />
                <p className={classes.textMuted}>No users registered yet</p>
            </div>
        ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
                {users.map(user => (
                    <div key={user.id || user._id} className="p-6 rounded-2xl bg-white/5 border border-white/5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center font-black text-purple-500 border border-purple-500/20">
                                {user.first_name?.[0] || user.email[0].toUpperCase()}
                            </div>
                            <div>
                                <p className="font-bold text-white text-sm truncate w-32">
                                    {user.first_name} {user.last_name}
                                </p>
                                <p className="text-xs text-zinc-500">{user.email}</p>
                                <span className="text-[10px] font-black uppercase text-purple-500">{user.role}</span>
                            </div>
                        </div>
                        <button className="p-2 text-zinc-500 hover:text-white">
                            <UserSearch size={18}/>
                        </button>
                    </div>
                ))}
            </div>
        )}
    </div>
);

// Main Component
const AdminDashboard = () => {
    const { theme } = useTheme();
    const [activeTab, setActiveTab] = useState('overview');
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [error, setError] = useState('');
    const [jobs, setJobs] = useState([]);
    const [users, setUsers] = useState([]);
    const [applications, setApplications] = useState([]);
    const [selectedApp, setSelectedApp] = useState(null);
    
    const [jobForm, setJobForm] = useState({
        title: '',
        department: '',
        location: 'Remote',
        type: 'Full-time',
        description: '',
        full_description: '',
        salary_range: '',
        requirements: '',
        responsibilities: ''
    });

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [statsData, jobsData, usersData, appsData] = await Promise.all([
                adminAPI.getDashboardStats().catch(() => null),
                adminAPI.getAllJobs().catch(() => []),
                adminAPI.getAllUsers().catch(() => []),
                adminAPI.getAllApplications().catch(() => [])
            ]);
            setStats(statsData);
            setJobs(jobsData || []);
            setUsers(usersData || []);
            setApplications(appsData || []);
        } catch (err) {
            console.error('Dashboard fetch error:', err);
            setError('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateJob = async () => {
        setSubmitLoading(true);
        setError('');
        
        const payload = {
            ...jobForm,
            requirements: typeof jobForm.requirements === 'string' 
                ? jobForm.requirements.split('\n').filter(r => r.trim() !== "") 
                : (jobForm.requirements || []),
            responsibilities: typeof jobForm.responsibilities === 'string' 
                ? jobForm.responsibilities.split('\n').filter(r => r.trim() !== "") 
                : (jobForm.responsibilities || []),
        };

        try {
            await adminAPI.createJob(payload);
            alert("✅ Job posted successfully!");
            setJobForm({
                title: '', department: '', location: 'Remote', type: 'Full-time',
                description: '', full_description: '', salary_range: '',
                requirements: '', responsibilities: ''
            });
            fetchInitialData();
            setActiveTab('jobs');
        } catch (err) {
            console.error("Job creation error:", err);
            setError(err.response?.data?.error || "Failed to create job");
            alert(`❌ Error: ${err.response?.data?.error || "Check console"}`);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleDeleteJob = async (jobId) => {
        if (!window.confirm('Are you sure you want to delete this job?')) return;
        
        try {
            await adminAPI.deleteJob(jobId);
            alert('✅ Job deleted successfully');
            fetchInitialData();
        } catch (err) {
            alert('❌ Failed to delete job');
        }
    };

    const handleUpdateApplication = async (appId, status) => {
        try {
            await adminAPI.updateApplicationStatus(appId, { status });
            fetchInitialData();
            setSelectedApp(null);
            alert('✅ Application updated successfully');
        } catch (err) {
            alert(`❌ Error: ${err.response?.data?.error || "Update failed"}`);
        }
    };

    const getThemeClasses = () => {
        if (theme === 'light') {
            return {
                bg: 'bg-gray-50', card: 'bg-white', border: 'border-gray-200',
                text: 'text-gray-900', textMuted: 'text-gray-500',
                input: 'bg-white border-gray-300 text-gray-900 focus:border-purple-500',
                button: 'bg-gradient-to-r from-purple-600 to-pink-600 text-white',
                tabActive: 'bg-purple-600 text-white shadow-md',
                tabInactive: 'text-gray-600 hover:bg-gray-100'
            };
        }
        return { 
            bg: 'bg-black', card: 'bg-zinc-900/50 backdrop-blur-xl', border: 'border-white/10',
            text: 'text-white', textMuted: 'text-zinc-400',
            input: 'bg-black/50 border-white/10 text-white focus:border-purple-500',
            button: 'bg-gradient-to-r from-purple-600 to-pink-600 shadow-lg shadow-purple-500/20',
            tabActive: 'bg-gradient-to-r from-purple-600 to-pink-600 text-white',
            tabInactive: 'text-zinc-400 hover:bg-white/5'
        };
    };

    const classes = getThemeClasses();

    if (loading) return (
        <div className={`min-h-screen ${classes.bg} flex items-center justify-center`}>
            <div className="text-center">
                <Loader className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className={classes.textMuted}>Loading dashboard...</p>
            </div>
        </div>
    );

    return (
        <div className={`min-h-screen ${classes.bg} pt-20 flex flex-col lg:flex-row`}>
            {/* Sidebar */}
            <div className={`w-full lg:w-72 ${classes.card} border-r ${classes.border} p-6 h-auto lg:h-[calc(100vh-80px)] lg:sticky lg:top-20`}>
                <div className="space-y-2">
                    <NavButton icon={<LayoutDashboard size={20}/>} label="Overview" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} classes={classes} />
                    <NavButton icon={<ClipboardList size={20}/>} label="Applicants" active={activeTab === 'apps'} onClick={() => setActiveTab('apps')} classes={classes} />
                    <NavButton icon={<Mail size={20}/>} label="Email" active={activeTab === 'email'} onClick={() => setActiveTab('email')} classes={classes} />
                    <NavButton icon={<Briefcase size={20}/>} label="Jobs" active={activeTab === 'jobs'} onClick={() => setActiveTab('jobs')} classes={classes} />
                    
                    <NavButton icon={<PlusCircle size={20}/>} label="Post Job" active={activeTab === 'post-job'} onClick={() => setActiveTab('post-job')} classes={classes} />
                    
                    <NavButton icon={<Users size={20}/>} label="Users" active={activeTab === 'users'} onClick={() => setActiveTab('users')} classes={classes} />
                    <NavButton 
    icon={<FileText size={20}/>} 
    label="Import Resumes" 
    active={activeTab === 'import-resumes'} 
    onClick={() => setActiveTab('import-resumes')} 
    classes={classes} 
/>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 p-6 md:p-10 overflow-y-auto">
                <div className="max-w-7xl mx-auto">
                    {error && (
                        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/50 rounded-xl text-red-500 flex items-center gap-3">
                            <AlertCircle size={20} />
                            <span>{error}</span>
                            <button onClick={() => setError('')} className="ml-auto">
                                <X size={18} />
                            </button>
                        </div>
                    )}

                    {activeTab === 'overview' && <OverviewTab stats={stats} applications={applications} classes={classes} />}
                    {activeTab === 'apps' && <ApplicationsTab applications={applications} classes={classes} onSelect={setSelectedApp} />}
                    {activeTab === 'jobs' && <JobsTab jobs={jobs} classes={classes} onDelete={handleDeleteJob} />}
                    {activeTab === 'post-job' && <PostJobTab jobForm={jobForm} setJobForm={setJobForm} classes={classes} onSubmit={handleCreateJob} loading={submitLoading} />}
                    {activeTab === 'users' && <UsersTab users={users} classes={classes} />}
                    {activeTab === 'import-resumes' && <ResumeManagement classes={classes} />}
                    {activeTab === 'email' && <AdminEmailDashboard />}
                </div>
            </div>

            {selectedApp && (
                <DetailModal 
                    app={selectedApp} 
                    classes={classes} 
                    onClose={() => setSelectedApp(null)} 
                    onUpdate={handleUpdateApplication}
                />
            )}
        </div>
    );
};

export default AdminDashboard;